/**
 * badge-pricing.js
 * -----------------------------------------------------------------------------
 * Patches two defects in the compiled `base.js` bundle without touching it:
 *
 *  1. getProductCardPriceHtml()
 *     - accepts an explicit `badge` object resolved in Liquid (authoritative),
 *       instead of depending on the SALE_BADGES global. SALE_BADGES is built from
 *       `shop.metaobjects.badge.values`, which is capped by Liquid (50) while the
 *       shop has 61 badge entries, so badges past the cap silently do not exist
 *       in JS. Liquid's `shop.metaobjects.badge[handle]` lookup has no such cap.
 *     - never compares a VARIANT compare-at price against a PRODUCT price_min.
 *     - picks the same badge Liquid picks when a product has several badge tags.
 *     - tolerates discount_percentage arriving as a number, a numeric string, or
 *       a metafield-shaped object ({ value: 5 }).
 *
 *  2. setActiveVariantId()
 *     - only rewrites the browser URL on the product template. Previously any
 *       swatch click on any collection card stamped `?variant=<id>` onto the
 *       collection URL.
 *
 * Load AFTER base.js. Safe to load on every template.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- helpers */

  function toCents(value) {
    if (value === null || value === undefined || value === '') return null;
    var n = typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    if (isNaN(n)) return null;
    return Math.round(n);
  }

  function escHtml(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
  }

  function isColor(value) {
    if (typeof value !== 'string') return false;
    var v = value.trim();
    return /^#[0-9a-fA-F]{3,8}$/.test(v) || /^rgba?\([\d.,\s%]+\)$/.test(v);
  }

  // Only let through values that are safe to drop into a style attribute.
  function safeColor(value) {
    return isColor(value) ? value.trim() : 'inherit';
  }

  /* ------------------------------------------------------- money formatting */
  /* Mirrors the formatter inside base.js, including its trailing ".00" trim,   */
  /* so patched and unpatched output stay visually identical.                   */

  function formatMoney(cents) {
    var format = (window.CURRENCY && window.CURRENCY.money_format) || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    if (!match) return format;

    function build(precision, thousands, decimal) {
      var parts = (cents / 100.0).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':                       value = build(0, ',', '.'); break;
      case 'amount_with_comma_separator':              value = build(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':  value = build(0, '.', ','); break;
      case 'amount_with_space_separator':              value = build(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator':  value = build(0, ' ', ','); break;
      case 'amount_with_apostrophe_separator':         value = build(2, "'", '.'); break;
      default:                                         value = build(2, ',', '.');
    }
    return format.replace(placeholder, value);
  }

  function money(cents) {
    if (cents === null || cents === undefined || isNaN(cents)) return '';
    var out = formatMoney(Math.round(cents));
    if (out.slice(-3) === '.00') out = out.slice(0, -3);
    return out;
  }

  /* --------------------------------------------------------- badge handling */

  function readDiscount(badge) {
    if (!badge) return 0;
    var raw = badge.discount_percentage;
    if (raw && typeof raw === 'object') raw = raw.value; // metaobject `| json` shape
    var n = parseFloat(raw);
    if (isNaN(n) || n <= 0 || n >= 100) return 0;
    return n;
  }

  /**
   * An explicit badge (resolved in Liquid) always wins. Otherwise fall back to
   * the SALE_BADGES global for call sites that only have tags (recently viewed,
   * Rebuy widgets). A badge carrying a discount beats a text-only badge, which
   * is the same rule the Liquid snippet uses — so both layers agree.
   */
  function resolveBadge(explicit, tags) {
    var map = window.SALE_BADGES || {};

    if (explicit) {
      // Backfill anything Liquid could not serialise (the Color metafield type
      // cannot go through `| json`) from the SALE_BADGES global, so a bad
      // colour degrades to the right colour rather than to `inherit`.
      var twin = explicit.handle && map[explicit.handle];
      if (!twin) return explicit;
      return {
        handle: explicit.handle,
        text: explicit.text || twin.text,
        text_color: isColor(explicit.text_color) ? explicit.text_color : twin.text_color,
        background_color: isColor(explicit.background_color) ? explicit.background_color : twin.background_color,
        discount_percentage: readDiscount(explicit) > 0 ? explicit.discount_percentage : twin.discount_percentage
      };
    }

    if (!Array.isArray(tags)) return null;

    var textOnly = null;

    for (var i = 0; i < tags.length; i++) {
      var tag = String(tags[i]);
      if (tag.indexOf('badge__') === -1) continue;

      var entry = map[tag.replace('badge__', '').trim().toLowerCase()];
      if (!entry) continue;

      if (readDiscount(entry) > 0) return entry;
      if (!textOnly) textOnly = entry;
    }
    return textOnly;
  }

  /* ------------------------------------------------------------ the renderer */

  /**
   * @param {number|string}  price                 cents — the ACTIVE VARIANT's price (preferred)
   * @param {number|string}  price_min             cents — product range low  (used only for "from")
   * @param {number|string}  price_max             cents — product range high (used only for "from")
   * @param {number|string}  compare_at_price_min  cents — the SAME variant's compare-at
   * @param {object}         badge                 resolved badge from Liquid (preferred)
   * @param {string[]}       tags                  fallback badge resolution via SALE_BADGES
   * @param {string}         class_size            tailwind size classes
   */
  function getProductCardPriceHtml(args) {
    args = args || {};

    var min     = toCents(args.price_min);
    var max     = toCents(args.price_max);
    var variant = toCents(args.price);
    var compare = toCents(args.compare_at_price_min);
    var sizeClass = args.class_size || '';

    // Products whose variants span a price range keep showing "from <low>".
    var isRange = min !== null && max !== null && max > min;

    var base = isRange ? min : (variant !== null ? variant : min);
    if (base === null) return '';

    // A compare-at belongs to ONE variant, so it may only be measured against
    // that variant's price — never against a product-level minimum. In range
    // mode there is no single price to measure it against, so it is skipped.
    var against = variant !== null ? variant : min;
    var hasCompare = !isRange && compare !== null && against !== null && compare > against;

    var badge = resolveBadge(args.badge, args.tags);
    var discount = readDiscount(badge) / 100;

    var current = discount > 0 ? Math.round(base * (1 - discount)) : base;
    var was = hasCompare ? compare : (discount > 0 ? base : null);
    var onSale = was !== null;

    var priceHtml =
      '<div class="' + (onSale ? 'text-orange' : 'text-charcoal') + ' font-serif ' + sizeClass +
      ' whitespace-nowrap tracking-wide">' +
        (isRange ? '<i>from</i> ' : '') + money(current) +
      '</div>';

    var wasHtml = was === null ? '' :
      '<div class="text-charcoal-3 line-through font-serif text-opacity-75 ' + sizeClass +
      ' whitespace-nowrap tracking-wide">' + money(was) + '</div>';

    var pillHtml = (badge && badge.text) ?
      '<div style="color: ' + safeColor(badge.text_color) +
      '; background: ' + safeColor(badge.background_color) + ';"' +
      ' class="flex items-center text-xs font-700 rounded-44 px-1.5 w-max py-0.5 mb-0.5 text-center">' +
      escHtml(badge.text) + '</div>' : '';

    return '<div class="flex items-center gap-2 w-max">' + priceHtml + wasHtml + '</div>' + pillHtml;
  }

  /* ---------------------------------------------- third-party overwrite guard */
  /*
   * The "B4B - Variants Products" app rewrites collection-card prices AFTER the
   * theme has rendered them: it deletes the strike-through element and replaces
   * the remaining price with Shopify's raw variant price, tagging what it
   * touched with `b4b-` classes. It cannot know the displayed price, because the
   * badge discount exists only in the theme — Shopify's price field says 12500.
   *
   * This guard re-evaluates the element's OWN x-html expression whenever its
   * content stops matching what that expression produces. Re-evaluating rather
   * than replaying a snapshot means a legitimate Alpine re-render (a swatch
   * click changing variant) is a no-op instead of a fight.
   *
   * Escape hatches: set window.BADGE_PRICING_GUARD = false before this file
   * loads to disable it entirely. Repairs are capped per element per page load
   * so a misbehaving app can never spin this into a loop.
   */

  var SELECTOR = '[x-html*="getProductCardPriceHtml"]';
  var MAX_REPAIRS = 10;

  var repairCount = new WeakMap();
  var pending = [];
  var frame = null;
  var writing = false;

  function reassert(el) {
    if (!el.isConnected) return;

    var count = repairCount.get(el) || 0;
    if (count >= MAX_REPAIRS) return;

    var expression = el.getAttribute('x-html');
    if (!expression) return;

    var html;
    try {
      html = window.Alpine.evaluate(el, expression);
    } catch (e) {
      return; // scope not ready, or the expression threw — leave the DOM alone
    }

    if (typeof html !== 'string' || html === '' || el.innerHTML === html) return;

    repairCount.set(el, count + 1);
    writing = true;
    el.innerHTML = html;
    writing = false;
  }

  function flush() {
    frame = null;
    var batch = pending;
    pending = [];
    for (var i = 0; i < batch.length; i++) reassert(batch[i]);
  }

  function queue(el) {
    if (pending.indexOf(el) === -1) pending.push(el);
    if (frame === null) frame = requestAnimationFrame(flush);
  }

  function collect(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.matches && node.matches(SELECTOR)) queue(node);
    if (node.querySelectorAll) {
      var found = node.querySelectorAll(SELECTOR);
      for (var i = 0; i < found.length; i++) queue(found[i]);
    }
  }

  function startGuard() {
    if (window.BADGE_PRICING_GUARD === false) return;
    if (!window.MutationObserver || !window.Alpine || !window.Alpine.evaluate) return;
    if (!document.body) return;

    new MutationObserver(function (records) {
      if (writing) return; // ignore the mutations we cause ourselves

      for (var i = 0; i < records.length; i++) {
        var target = records[i].target;
        var el = target.nodeType === 1 ? target : target.parentElement;
        var host = el && el.closest && el.closest(SELECTOR);
        if (host) queue(host);

        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) collect(added[j]);
      }
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  /* -------------------------------------------------------------- installer */

  function isProductTemplate() {
    return !!document.body && /(^|\s)template-product(\s|$)/.test(document.body.className);
  }

  function install() {
    var store = window.Alpine && window.Alpine.store && window.Alpine.store('main');
    if (!store || store.__badgePricingPatched) return;

    store.getProductCardPriceHtml = getProductCardPriceHtml;

    var originalSetActiveVariantId = store.setActiveVariantId.bind(store);
    store.setActiveVariantId = function (id) {
      // Keep the reactive state everywhere; only the product page owns the URL.
      if (isProductTemplate()) return originalSetActiveVariantId(id);
      this.activeProductVariantId = id;
    };

    store.__badgePricingPatched = true;

    if (document.body) startGuard();
    else document.addEventListener('DOMContentLoaded', startGuard);
  }

  if (window.Alpine && window.Alpine.store && window.Alpine.store('main')) {
    install();
  } else {
    document.addEventListener('alpine:init', install);
  }
})();